/**
 * <skincu-swipe-hint for="carousel-id" cue>
 *
 * The row of controls under a Skincu carousel. It never moves the cards itself.
 * - hides itself when the row of cards does not scroll (a desktop grid, for instance)
 * - once the visitor has moved the carousel, it adds "is-swiped", which stops the
 *   drifting arrow in the next button and fades out the Swipe label
 */
if (!customElements.get('skincu-swipe-hint')) {
  class SkincuSwipeHint extends HTMLElement {
    connectedCallback() {
      this.carousel = document.getElementById(this.getAttribute('for'));

      if (!this.carousel) return;

      this._abort = new AbortController();
      this._startLeft = this.carousel.scrollLeft;

      this.checkScrollable();

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => this.checkScrollable());
        this._resizeObserver.observe(this.carousel);
      }

      this.carousel.addEventListener(
        'scroll',
        () => {
          if (Math.abs(this.carousel.scrollLeft - this._startLeft) > 24) this.dismissLabel();
        },
        { passive: true, signal: this._abort.signal }
      );
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._resizeObserver) this._resizeObserver.disconnect();
    }

    get isScrollable() {
      return this.carousel.scrollWidth - this.carousel.clientWidth > 1;
    }

    checkScrollable() {
      this.toggleAttribute('hidden', !this.isScrollable);
    }

    dismissLabel() {
      if (this._abort) this._abort.abort();

      this.collapseLabel();
      this.settleArrow();
    }

    // The label shrinks to nothing while it fades, so the arrows glide to their new
    // place instead of jumping when the label leaves the row.
    collapseLabel() {
      const label = this.querySelector('.skincu-carousel-nav__label');

      if (!label) return;

      label.style.maxWidth = `${label.scrollWidth}px`;
      label.getBoundingClientRect(); // lock the start width before the transition runs
      label.classList.add('is-dismissed');
    }

    // Lets the drifting arrow finish the loop it is in, so it comes to rest at its
    // starting point instead of snapping back halfway.
    settleArrow() {
      const icon = this.querySelector('carousel-next-button .icon');
      const stop = () => this.classList.add('is-swiped');

      if (!icon || !icon.getAnimations || icon.getAnimations().length === 0) {
        stop();
        return;
      }

      icon.addEventListener('animationiteration', stop, { once: true });
      setTimeout(stop, 1800);
    }
  }

  customElements.define('skincu-swipe-hint', SkincuSwipeHint);
}

/**
 * <skincu-scroll-controls for="scroll-area-id" cue>
 *
 * The same row of controls for a plain scrolling area such as the compare table, where
 * there are no slides to count. The arrows move the area by most of its visible width
 * and the progress bar follows the scroll position. The row stays hidden while the
 * area fits without scrolling.
 */
if (!customElements.get('skincu-scroll-controls')) {
  class SkincuScrollControls extends HTMLElement {
    connectedCallback() {
      this.area = document.getElementById(this.getAttribute('for'));

      if (!this.area) return;

      this.thumb = this.querySelector('[data-scroll-thumb]');
      this.prevButton = this.querySelector('[data-scroll-prev]');
      this.nextButton = this.querySelector('[data-scroll-next]');
      this.markers = Array.from(this.querySelectorAll('[data-scroll-marker]'));
      this._abort = new AbortController();
      this._hasMoved = false;

      const options = { signal: this._abort.signal };

      this.prevButton?.addEventListener('click', () => this.move(-1), options);
      this.nextButton?.addEventListener('click', () => this.move(1), options);
      this.markers.forEach((marker, index) => marker.addEventListener('click', () => this.goTo(index), options));
      this.area.addEventListener('scroll', () => this.update(), { passive: true, signal: this._abort.signal });

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => this.update());
        this._resizeObserver.observe(this.area);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._resizeObserver) this._resizeObserver.disconnect();
    }

    get maxScroll() {
      return this.area.scrollWidth - this.area.clientWidth;
    }

    get position() {
      // scrollLeft runs negative in right to left pages
      return Math.abs(this.area.scrollLeft);
    }

    // With lines, every line stands for an evenly spaced position between the start and
    // the end of the row, so the last line is always reachable and always lights up.
    get markerIndex() {
      const steps = this.markers.length - 1;

      return steps > 0 ? Math.round((this.position / Math.max(this.maxScroll, 1)) * steps) : 0;
    }

    goTo(index) {
      const rtl = getComputedStyle(this.area).direction === 'rtl' ? -1 : 1;
      const steps = Math.max(this.markers.length - 1, 1);

      this.area.scrollTo({ left: rtl * (this.maxScroll * index) / steps, behavior: 'smooth' });
    }

    move(direction) {
      if (this.markers.length > 1) {
        this.goTo(Math.min(Math.max(this.markerIndex + direction, 0), this.markers.length - 1));
        return;
      }

      const rtl = getComputedStyle(this.area).direction === 'rtl' ? -1 : 1;

      this.area.scrollBy({ left: direction * rtl * this.area.clientWidth * 0.7, behavior: 'smooth' });
    }

    update() {
      const max = this.maxScroll;

      this.toggleAttribute('hidden', max <= 1);

      if (max <= 1) return;

      const visibleShare = this.area.clientWidth / this.area.scrollWidth;
      const progress = Math.min(this.position / max, 1);

      if (this.thumb) {
        this.thumb.style.width = `${visibleShare * 100}%`;
        this.thumb.style.transform = `translateX(${(progress * (1 - visibleShare) * 100) / visibleShare}%)`;
      }

      this.markers.forEach((marker, index) => marker.setAttribute('aria-current', index === this.markerIndex ? 'true' : 'false'));

      if (this.prevButton) this.prevButton.disabled = this.position <= 1;
      if (this.nextButton) this.nextButton.disabled = this.position >= max - 1;

      if (!this._hasMoved && this.position > 24) {
        this._hasMoved = true;

        const label = this.querySelector('.skincu-carousel-nav__label');

        if (label) {
          label.style.maxWidth = `${label.scrollWidth}px`;
          label.getBoundingClientRect();
          label.classList.add('is-dismissed');
        }

        const icon = this.nextButton?.querySelector('.icon');
        const stop = () => this.classList.add('is-swiped');

        if (icon && icon.getAnimations && icon.getAnimations().length > 0) {
          icon.addEventListener('animationiteration', stop, { once: true });
          setTimeout(stop, 1800);
        } else {
          stop();
        }
      }
    }
  }

  customElements.define('skincu-scroll-controls', SkincuScrollControls);
}
